// TMOS Module Page
// Date: December 9, 2025
// Purpose: Displays all screens/functionality for a selected module
//          Supports recursive multi-level modules: /modules/{id}/{id2}/{id3}...
//          Users navigate to modules from the sidebar, then see all screens and sub-modules within that module

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import { iconService } from '@/services/iconService';
import { ArrowLeftIcon, FolderIcon } from '@heroicons/react/24/outline';

export const ModulePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [module, setModule] = useState<any>(null);
  const [screens, setScreens] = useState<any[]>([]);
  const [subModules, setSubModules] = useState<any[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ name: string; path: string }>>([]);
  const [loading, setLoading] = useState(true);

  // Parse the module path from URL (e.g., /modules/module1/submodule2 -> ['module1', 'submodule2'])
  const getModulePath = (): string[] => {
    const pathMatch = location.pathname.match(/^\/modules\/(.+)$/);
    if (!pathMatch) return [];
    return pathMatch[1].split('/').filter(Boolean);
  };

  // Recursively find a module in the hierarchy by path
  const findModuleByPath = (modules: any[], path: string[]): any | null => {
    if (path.length === 0) return null;
    
    const [firstCode, ...restPath] = path;
    const normalizedFirstCode = firstCode.toLowerCase().replace(/_/g, '-');
    
    // Find the module with matching code
    const foundModule = modules.find((m: any) => {
      const normalizedCode = m.module_code.toLowerCase().replace(/_/g, '-');
      return normalizedCode === normalizedFirstCode;
    });

    if (!foundModule) {
      return null;
    }

    // If this is the last item in the path, return this module
    if (restPath.length === 0) {
      return foundModule;
    }

    // Otherwise, recursively search in children (which can be modules or screens)
    const childModules = (foundModule.children || []).filter((child: any) => 
      child.module_code !== undefined // It's a module, not a screen
    );

    return findModuleByPath(childModules, restPath);
  };

  // Build breadcrumbs from the path
  const buildBreadcrumbs = (modules: any[], path: string[]): Array<{ name: string; path: string }> => {
    const crumbs: Array<{ name: string; path: string }> = [
      { name: t('navigation.menuItems.dashboard') || 'Dashboard', path: '/dashboard' }
    ];

    let currentModules = modules;
    let currentPath = '';

    path.forEach((code) => {
      const normalizedCode = code.toLowerCase().replace(/_/g, '-');
      const module = currentModules.find((m: any) => {
        const normalizedModuleCode = m.module_code.toLowerCase().replace(/_/g, '-');
        return normalizedModuleCode === normalizedCode;
      });

      if (module) {
        currentPath += (currentPath ? '/' : '') + code;
        crumbs.push({
          name: module.display_name || module.translation_key || code,
          path: `/modules/${currentPath}`
        });

        // Update currentModules for next iteration
        currentModules = (module.children || []).filter((child: any) => 
          child.module_code !== undefined
        );
      }
    });

    return crumbs;
  };

  useEffect(() => {
    const loadModuleData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const modulePath = getModulePath();
        if (modulePath.length === 0) {
          setLoading(false);
          return;
        }

        // Determine category - try both BUSINESS and ADMIN
        const [businessTree, adminTree] = await Promise.all([
          unifiedRBACService.getCurrentUserNavigation('BUSINESS'),
          unifiedRBACService.getCurrentUserNavigation('ADMIN')
        ]);

        // Combine all modules from both trees
        const allModules = [...businessTree.modules, ...adminTree.modules];

        // Find the module by navigating through the path
        const foundModule = findModuleByPath(allModules, modulePath);

        if (foundModule) {
          setModule(foundModule);
          
          // Build breadcrumbs
          const crumbs = buildBreadcrumbs(allModules, modulePath);
          setBreadcrumbs(crumbs);

          // Separate screens and sub-modules from children
          const children = foundModule.children || [];
          
          const permittedScreens = children
            .filter((child: any) => 
              child.screen_code !== undefined && // It's a screen
              child.has_read_permission === true
            )
            // Sort screens by their own sort_order (they all belong to the same parent module)
            .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
          
          const permittedSubModules = children
            .filter((child: any) => 
              child.module_code !== undefined && // It's a module
              child.is_active !== false // Module is active
            )
            // Sort sub-modules by their sort_order
            .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));

          setScreens(permittedScreens);
          setSubModules(permittedSubModules);
          
          // If there's only one screen and no sub-modules, automatically redirect to it
          if (permittedScreens.length === 1 && permittedSubModules.length === 0 && permittedScreens[0].route_path) {
            navigate(permittedScreens[0].route_path, { replace: true });
            return;
          }
        } else {
          // Module not found
          setModule(null);
        }
      } catch (error) {
        console.error('Error loading module data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadModuleData();
  }, [location.pathname, user?.id]);

  const getTranslatedText = (displayName: string, translationKey?: string): string => {
    if (translationKey) {
      const translated = t(translationKey);
      return translated !== translationKey ? translated : displayName;
    }
    return displayName;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-[var(--color-primary-text)]">
          {t('navigation.loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="border border-[var(--color-border)] rounded-lg p-6">
          <h2 className="text-xl font-bold text-[var(--color-surface-text)] mb-4">
            {t('module.notFound') || 'Module Not Found'}
          </h2>
          <p className="text-[var(--color-secondary-text)]">
            {t('module.notFoundMessage') || 'The requested module could not be found or you do not have access to it.'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 border border-[var(--color-border)] text-[var(--color-surface-text)] rounded hover:border-[var(--color-accent-bg)] transition-colors"
          >
            {t('navigation.backToDashboard') || 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  // Get the module path for building sub-module links
  const getCurrentModulePath = (): string => {
    const path = getModulePath();
    return path.join('/');
  };

  // Build path for a sub-module
  const getSubModulePath = (subModuleCode: string): string => {
    const currentPath = getCurrentModulePath();
    return `/modules/${currentPath ? currentPath + '/' : ''}${subModuleCode.toLowerCase().replace(/_/g, '-')}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <nav className="mb-4 flex items-center space-x-2 text-sm">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              {index > 0 && <span className="text-[var(--color-secondary-text)]">/</span>}
              {index === breadcrumbs.length - 1 ? (
                <span className="text-[var(--color-primary-text)] font-medium">{crumb.name}</span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)]"
                >
                  {crumb.name}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Header */}
      <div className="mb-8">
        {breadcrumbs.length > 1 && (
          <button
            onClick={() => {
              // Navigate to parent module (second-to-last breadcrumb)
              const parentPath = breadcrumbs[breadcrumbs.length - 2]?.path || '/dashboard';
              navigate(parentPath);
            }}
            className="flex items-center space-x-2 text-[var(--color-primary-text)] hover:text-[var(--color-secondary-text)] mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>{t('navigation.back') || 'Back'}</span>
          </button>
        )}
        
        <div className="flex items-center space-x-3">
          {module.icon && iconService.isIconAvailable(module.icon) ? (
            React.createElement(iconService.getIcon(module.icon), { className: 'w-8 h-8 text-[var(--color-primary-text)]' })
          ) : (
            <div className="w-8 h-8 bg-[var(--color-secondary-bg)] rounded flex items-center justify-center">
              <span className="text-[var(--color-secondary-text)] text-lg">📁</span>
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold text-[var(--color-primary-text)]">
              {getTranslatedText(module.display_name, module.translation_key)}
            </h1>
            {module.description && (
              <p className="text-[var(--color-secondary-text)] mt-1">
                {module.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Sub-Modules and Screens Grid */}
      {subModules.length > 0 || screens.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Sub-Modules */}
          {subModules.map((subModule: any) => {
            const subModulePath = getSubModulePath(subModule.module_code);
            const childCount = (subModule.children || []).length;
            
            return (
              <Link
                key={subModule.id}
                to={subModulePath}
                className="border border-[var(--color-border)] rounded-lg p-6 hover:border-[var(--color-accent-bg)] transition-colors"
              >
                <div className="flex items-start space-x-4">
                  {subModule.icon && iconService.isIconAvailable(subModule.icon) ? (
                    React.createElement(iconService.getIcon(subModule.icon), { 
                      className: 'w-8 h-8 text-[var(--color-primary-text)] flex-shrink-0' 
                    })
                  ) : (
                    <div className="w-8 h-8 bg-[var(--color-secondary-bg)] rounded flex items-center justify-center flex-shrink-0">
                      <FolderIcon className="w-5 h-5 text-[var(--color-secondary-text)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-[var(--color-primary-text)] mb-1">
                      {getTranslatedText(subModule.display_name, subModule.translation_key)}
                    </h3>
                    {subModule.description && (
                      <p className="text-sm text-[var(--color-secondary-text)] line-clamp-2 mb-2">
                        {subModule.description}
                      </p>
                    )}
                    <p className="text-xs text-[var(--color-secondary-text)]">
                      {childCount} {childCount === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}

          {/* Screens */}
          {screens.map((screen: any) => (
            <Link
              key={screen.id}
              to={screen.route_path || `#`}
              className="border border-[var(--color-border)] rounded-lg p-6 hover:border-[var(--color-accent-bg)] transition-colors"
            >
              <div className="flex items-start space-x-4">
                {screen.icon && iconService.isIconAvailable(screen.icon) ? (
                  React.createElement(iconService.getIcon(screen.icon), { 
                    className: 'w-8 h-8 text-[var(--color-primary-text)] flex-shrink-0' 
                  })
                ) : (
                  <div className="w-8 h-8 bg-[var(--color-secondary-bg)] rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-[var(--color-secondary-text)]">📄</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-[var(--color-primary-text)] mb-1">
                    {getTranslatedText(screen.display_name, screen.translation_key)}
                  </h3>
                  {screen.description && (
                    <p className="text-sm text-[var(--color-secondary-text)] line-clamp-2">
                      {screen.description}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="border border-[var(--color-border)] rounded-lg p-6">
          <p className="text-[var(--color-surface-text)]">
            {t('module.noScreens') || 'No screens or sub-modules available in this module.'}
          </p>
        </div>
      )}
    </div>
  );
};

